(() => {
  if (customElements.get('tccs-product-rotator')) return;

  class TccsProductRotator extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-rotator-track]');
      this.slides = Array.from(this.querySelectorAll('[data-rotator-slide]'));
      this.controls = this.querySelector('[data-rotator-controls]');
      this.previousButton = this.querySelector('[data-rotator-previous]');
      this.nextButton = this.querySelector('[data-rotator-next]');
      this.toggleButton = this.querySelector('[data-rotator-toggle]');
      this.status = this.querySelector('[data-rotator-status]');

      if (!this.track || this.slides.length === 0) {
        if (this.controls) this.controls.hidden = true;
        return;
      }

      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.interval = Math.max(4000, Number(this.dataset.interval) || 6000);
      this.userPaused = false;
      this.hoverPaused = false;
      this.focusPaused = false;
      this.pointerPaused = false;
      this.inView = true;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      if (this.slides.length < 2) {
        if (this.controls) this.controls.hidden = true;
        return;
      }

      this.bindEvents();
      this.updateMotionPreference();
      this.observeVisibility();
      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      window.clearTimeout(this.scrollTimeout);
      window.clearTimeout(this.pointerTimeout);
      if (this.abortController) this.abortController.abort();
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    bindEvents() {
      this.previousButton?.addEventListener('click', () => this.move(-1, true), { signal: this.signal });
      this.nextButton?.addEventListener('click', () => this.move(1, true), { signal: this.signal });
      this.toggleButton?.addEventListener('click', () => this.toggleAutoplay(), { signal: this.signal });

      this.addEventListener('mouseenter', () => {
        this.hoverPaused = true;
        this.syncAutoplay();
      }, { signal: this.signal });

      this.addEventListener('mouseleave', () => {
        this.hoverPaused = false;
        this.syncAutoplay();
      }, { signal: this.signal });

      this.addEventListener('focusin', () => {
        this.focusPaused = true;
        this.syncAutoplay();
      }, { signal: this.signal });

      this.addEventListener('focusout', () => {
        window.setTimeout(() => {
          this.focusPaused = this.contains(document.activeElement);
          this.syncAutoplay();
        }, 0);
      }, { signal: this.signal });

      this.track.addEventListener('keydown', (event) => {
        if (event.target !== this.track) return;
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.move(-1, true);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.move(1, true);
        }
      }, { signal: this.signal });

      this.track.addEventListener('pointerdown', () => {
        this.pointerPaused = true;
        window.clearTimeout(this.pointerTimeout);
        this.syncAutoplay();
      }, { signal: this.signal });

      const releasePointerPause = () => {
        window.clearTimeout(this.pointerTimeout);
        this.pointerTimeout = window.setTimeout(() => {
          this.pointerPaused = false;
          this.syncAutoplay();
        }, 3000);
      };

      this.track.addEventListener('pointerup', releasePointerPause, { signal: this.signal });
      this.track.addEventListener('pointercancel', releasePointerPause, { signal: this.signal });

      document.addEventListener('visibilitychange', () => this.syncAutoplay(), { signal: this.signal });

      const motionHandler = () => this.updateMotionPreference();
      if (this.reducedMotion.addEventListener) {
        this.reducedMotion.addEventListener('change', motionHandler, { signal: this.signal });
      } else {
        this.reducedMotion.addListener(motionHandler);
      }

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateStatus(this.currentIndex(), false));
        this.resizeObserver.observe(this.track);
      }
    }

    observeVisibility() {
      if (!('IntersectionObserver' in window)) return;
      this.intersectionObserver = new IntersectionObserver((entries) => {
        this.inView = entries[0]?.isIntersecting ?? true;
        this.syncAutoplay();
      }, { threshold: 0.25 });
      this.intersectionObserver.observe(this);
    }

    updateMotionPreference() {
      this.dataset.reducedMotion = String(this.reducedMotion.matches);
      if (this.toggleButton) {
        this.toggleButton.hidden = !this.autoplayEnabled || this.reducedMotion.matches;
      }
      this.syncAutoplay();
    }

    toggleAutoplay() {
      this.userPaused = !this.userPaused;
      this.classList.toggle('is-user-paused', this.userPaused);
      this.toggleButton.setAttribute('aria-pressed', String(this.userPaused));
      this.toggleButton.setAttribute(
        'aria-label',
        this.userPaused ? 'Resume automatic rotation' : 'Pause automatic rotation'
      );
      this.syncAutoplay();
    }

    canAutoplay() {
      return this.autoplayEnabled
        && !this.reducedMotion.matches
        && !this.userPaused
        && !this.hoverPaused
        && !this.focusPaused
        && !this.pointerPaused
        && this.inView
        && !document.hidden;
    }

    startAutoplay() {
      if (!this.canAutoplay() || this.timer) return;
      this.timer = window.setInterval(() => this.move(1, false), this.interval);
    }

    stopAutoplay() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }

    syncAutoplay() {
      if (this.canAutoplay()) this.startAutoplay();
      else this.stopAutoplay();
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }

    visibleCount() {
      const firstSlide = this.slides[0];
      const slideWidth = firstSlide.getBoundingClientRect().width;
      const styles = window.getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      if (!slideWidth) return 1;
      return Math.max(1, Math.floor((this.track.clientWidth + gap + 1) / (slideWidth + gap)));
    }

    currentIndex() {
      const trackLeft = this.track.getBoundingClientRect().left;
      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      return nearestIndex;
    }

    move(direction, announce) {
      const visible = this.visibleCount();
      const current = this.currentIndex();
      let target = current + (direction * visible);

      if (target >= this.slides.length) target = 0;
      if (target < 0) target = Math.max(0, this.slides.length - visible);

      this.track.scrollTo({
        left: this.slides[target].offsetLeft,
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth'
      });

      this.updateStatus(target, announce);
      if (announce) this.restartAutoplay();
    }

    updateStatus(startIndex, announce) {
      if (!announce || !this.status) return;
      const first = startIndex + 1;
      const last = Math.min(this.slides.length, startIndex + this.visibleCount());
      this.status.textContent = `Showing products ${first} through ${last} of ${this.slides.length}.`;
    }
  }

  customElements.define('tccs-product-rotator', TccsProductRotator);
})();
